import React from "react";
import { useTranslate } from "@refinedev/core";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Layout,
  Result,
  Row,
  Spin,
  notification,
  theme,
} from "antd";
import ReCAPTCHA from "react-google-recaptcha";
import { axiosInstance } from "../../authProvider";
import { isCaptchaEnabled } from "../../shared/captcha";
import { PageTitle } from "../login/components/PageTitle";
import { AuthHeroPanel } from "../login/components/AuthHeroPanel";

const { useToken } = theme;

/**
 * Requests a fresh activation link. Reached from the login-page alert, the signup
 * success screen, and an expired activation link — hence a linkable page with an
 * `?email=` prefill rather than a modal.
 */
const ResendActivation = () => {
  const _t = useTranslate();
  const { token } = useToken();
  const [form] = Form.useForm();
  const [params] = useSearchParams();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [success, setSuccess] = React.useState<boolean>(false);
  const [notif, contextHolder] = notification.useNotification();
  const [recaptchaValue, setRecaptchaValue] = React.useState<string | null>(null);
  const recaptchaRef = React.useRef<any>(null);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      await axiosInstance.post("/tenant-signup/resend-activation", {
        email: values.email,
        recaptchaToken: recaptchaValue ?? undefined,
      });
      // Always the same neutral outcome: the endpoint deliberately returns 204 whether
      // or not the address exists, and the UI must not leak the difference either.
      setSuccess(true);
    } catch (error: any) {
      if (error?.response?.data?.errors?.recaptcha === "captchaFailed") {
        recaptchaRef.current?.reset();
        setRecaptchaValue(null);
        notif.error({
          message: _t("pages.register.errors.title"),
          description: _t("pages.tenantSignup.errors.captchaFailed"),
        });
      } else {
        notif.error({
          message: _t("pages.register.errors.title"),
          description: _t("pages.register.errors.unknown"),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Row
        justify="center"
        align="middle"
        style={{ minHeight: "100vh", backgroundColor: token.colorBgLayout }}
      >
        <Col className="sm:w-100 md:w-1/2">
          <Card
            className="mt-16 text-center"
            style={{
              backgroundColor: token.colorBgContainer,
              borderColor: token.colorBorder,
            }}
          >
            <Result
              status="success"
              title={_t("pages.tenantSignup.resend.successMessage")}
              subTitle={_t("pages.tenantSignup.resend.successSubMessage")}
              extra={
                <Link to="/login">{_t("pages.register.gotoLoginMessage")}</Link>
              }
            />
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <>
      {contextHolder}
      <Layout style={{ backgroundColor: token.colorBgLayout }}>
        <Row style={{ minHeight: "100dvh" }}>
          <Col
            xs={24}
            lg={10}
            className="overflow-y-auto"
            style={{
              padding: "16px",
              paddingTop: "128px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              backgroundColor: token.colorBgContainer,
            }}
          >
            <div className="w-full py-4" style={{ maxWidth: 600 }}>
              <PageTitle />
              <Card
                title={_t("pages.tenantSignup.resend.title")}
                className="mt-8"
                style={{
                  backgroundColor: token.colorBgContainer,
                  borderColor: token.colorBorder,
                  color: token.colorText,
                }}
              >
                <p style={{ color: token.colorTextSecondary }}>
                  {_t("pages.tenantSignup.resend.helpMessage")}
                </p>
                <Form
                  form={form}
                  name="resend-activation"
                  layout="vertical"
                  initialValues={{ email: params.get("email") ?? "" }}
                  onFinish={onFinish}
                  autoComplete="off"
                >
                  <Form.Item
                    name="email"
                    label={_t("users.fields.email")}
                    rules={[
                      {
                        required: true,
                        message: _t("pages.tenantSignup.errors.required"),
                      },
                      {
                        type: "email",
                        message: _t("pages.register.errors.invalidEmail"),
                      },
                    ]}
                  >
                    <Input size="large" />
                  </Form.Item>

                  {isCaptchaEnabled && (
                    <Form.Item>
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                        onChange={setRecaptchaValue}
                      />
                    </Form.Item>
                  )}

                  <Form.Item>
                    <Spin spinning={loading}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        block
                        disabled={isCaptchaEnabled && !recaptchaValue}
                      >
                        {_t("pages.tenantSignup.resend.submit")}
                      </Button>
                    </Spin>
                  </Form.Item>

                  <div className="text-center">
                    <Link to="/login">
                      {_t("pages.tenantSignup.buttons.haveAccount")}
                    </Link>
                  </div>
                </Form>
              </Card>
            </div>
          </Col>
          <AuthHeroPanel />
        </Row>
      </Layout>
    </>
  );
};

export default ResendActivation;
